import sumBy from 'lodash/sumBy';
import isNumber from 'lodash/isNumber';
import isNaN from 'lodash/isNaN';
import request from '../utils/request';
import baseApi from './baseApi';
import { formatFloat } from '../utils/number';
import { formatMsToMinutes, formatSecondsToMinutes } from '../containers/Dashboard/ChickenDashboard/ChartCard/utils';
import { makeChartData } from '../utils/dashboard';
import { buildApiPayload } from '../utils/api';
import { getObject } from '../utils/typeUtils';
import { TagTypes } from '../constants/api.constants';

export function getAnalytic(url, params, signal) {
  return request({
    url: `/analytic${url}`,
    method: 'get',
    data: params,
    signal,
  });
}

function makeApiPayload(payload) {
  return payload ? buildApiPayload({ payload, dateEndKeys: ['endTime'] }) : payload;
}

function transformConveyorDistResponse(res) {
  const totalConveyors = sumBy(res.distributions, 'count');
  
  const distributions = res.distributions.map((item) => ({
    ...item,
    value: item.count,
    share: totalConveyors ? item.count / totalConveyors : 0,
    material: item.materialName,
  }));
  
  return {
    distributions,
    total: res.total,
  };
}

function transformCountResponse(res) {
  let totalCount = 0;
  let totalShare = 0;
  
  const distributions = res.map(item => {
    const { totalCount: value, totalShare: share, materialName, ...rest } = item;
    totalCount += value;
    totalShare += share;
    
    return {
      ...rest,
      value,
      share,
      material: materialName,
    };
  });
  
  return {
    distributions,
    total: {
      count: totalCount,
      share: totalShare,
    },
  };
}

function transformWeightResponse(res) {
  let totalWeight = 0;
  
  const distributions = res.map(item => {
    const { distributionValue, ...rest } = item;
    totalWeight += distributionValue;
    
    return {
      ...rest,
      value: distributionValue,
    };
  });
  
  return {
    distributions,
    total: {
      weight: totalWeight,
    },
  };
}

function transformIntervalResponse(res, meta, arg, customFormat = val => val) {
  const materials = String(arg.materials).split(',');
  
  return {
    chartData: makeChartData(res, materials, customFormat),
    originalData: res,
  };
}

function transformCombinedIntervalResponse(res, meta, arg, customFormat) {
  const intervalsPayload = transformIntervalResponse(res.analytic, meta, arg, customFormat);
  const totalIntervalsPayload = transformIntervalResponse(res.analyticAccumulative, meta, arg, customFormat);
  
  return { ...intervalsPayload, chartTotalData: totalIntervalsPayload.chartData };
}

const dashboardAnalyticsApi = baseApi.injectEndpoints({
  endpoints: builder => ({
    getInboundConveyorContainers: builder.query({
      query: data => ({
        url: '/analytic/inbound-conveyor/containers',
        method: 'GET',
        data: makeApiPayload(data),
      }),
    }),
    getOutboundConveyorContainers: builder.query({
      query: data => ({
        url: '/analytic/outbound-conveyor/containers',
        method: 'GET',
        data: makeApiPayload(data),
      }),
    }),
    getInboundConveyorDistribution: builder.query({
      query: data => ({
        url: '/analytic/inbound-conveyor/total',
        method: 'GET',
        data: makeApiPayload(data),
      }),
      transformResponse: transformConveyorDistResponse,
      keepUnusedDataFor: 300,
    }),
    getOutboundConveyorDistribution: builder.query({
      query: data => ({
        url: '/analytic/outbound-conveyor/total',
        method: 'GET',
        data: makeApiPayload(data),
      }),
      transformResponse: transformConveyorDistResponse,
      keepUnusedDataFor: 300,
    }),
    getPerformanceRatio: builder.query({
      query: data => ({
        url: '/analytic/performance-ratio',
        method: 'GET',
        data: makeApiPayload(data),
      }),
      keepUnusedDataFor: 300,
    }),
    getPerformanceTriangle: builder.query({
      query: data => ({
        url: '/analytic/performance-triangle',
        method: 'GET',
        data: makeApiPayload(data),
      }),
      extraOptions: {
        takeLatest: true,
      },
      keepUnusedDataFor: 300,
      providesTags: [TagTypes.PerformanceTriangle],
      transformResponse: res => {
        const byMaterial = getObject(res.byMaterial);
        const total = res.total;

        const mapEntries = entry => {
          const [key, value] = entry;
          const newValue = isNumber(value) && !isNaN(value) ? value : 0;

          return [key, newValue];
        };

        const pTriangleEntries = Object.entries(byMaterial).map(entry => {
          const [key, value] = entry;
          const material = getObject(value);
          const materialEntries = Object.entries(material).map(mapEntries);

          return [key, Object.fromEntries(materialEntries)];
        });

        return {
          pTriangle: pTriangleEntries.length ? Object.fromEntries(pTriangleEntries) : null,
          pTriangleTotal: total ? Object.fromEntries(Object.entries(total).map(mapEntries)) : null,
        };
      },
    }),
    getPerformanceTriangleObjectives: builder.query({
      query: () => ({
        url: '/analytic/performance-triangle/objectives',
        method: 'GET',
      }),
      extraOptions: {
        takeLatest: true,
      },
      keepUnusedDataFor: 300,
      providesTags: [TagTypes.PerformanceTriangleObjectives],
    }),
    postPerformanceTriangleObjectives: builder.mutation({
      query: ({ data }) => ({
        url: '/analytic/performance-triangle/objectives',
        method: 'POST',
        data,
      }),
      invalidatesTags: (request, error) => {
        if (error) {
          return [];
        }

        return [TagTypes.PerformanceTriangle, TagTypes.PerformanceTriangleObjectives];
      },
    }),
    getPRSDistribution: builder.query({
      query: data => ({
        url: '/analytic/prs/total',
        method: 'GET',
        data: makeApiPayload(data),
      }),
      extraOptions: {
        takeLatest: true,
      },
      keepUnusedDataFor: 300,
    }),
    getPRSContainers: builder.query({
      query: data => ({
        url: '/analytic/prs/containers',
        method: 'GET',
        data: makeApiPayload(data),
      }),
      extraOptions: {
        takeLatest: true,
      },
    }),
    getExcessDistribution: builder.query({
      query: data => ({
        url: '/analytic/excess-distribution/total',
        method: 'GET',
        data: makeApiPayload(data),
      }),
      extraOptions: {
        takeLatest: true,
      },
      keepUnusedDataFor: 300,
    }),
    getStockOutDistribution: builder.query({
      query: data => ({
        url: '/stockOut/total',
        method: 'GET',
        data: makeApiPayload(data),
      }),
      extraOptions: {
        takeLatest: true,
      },
      transformResponse: res => {
        const distributions = [];
        let totalTimeMinutes = 0;
        let totalIncidents = 0;

        for (const item of res) {
          const timeMinutes = formatSecondsToMinutes(item.time);

          totalTimeMinutes += formatFloat(timeMinutes);
          totalIncidents += item.incidentCount;
          distributions.push({ ...item, timeMinutes });
        }

        return {
          distributions,
          total: {
            timeMinutes: totalTimeMinutes,
            incidentCount: totalIncidents,
          },
        };
      },
      keepUnusedDataFor: 300,
    }),
    getFryerCyclesDistribution: builder.query({
      query: data => ({
        url: '/analytic/fryer-cycles-distribution/total',
        method: 'GET',
        data: makeApiPayload(data),
      }),
      extraOptions: {
        takeLatest: true,
      },
      keepUnusedDataFor: 300,
    }),
    getWasteDistribution: builder.query({
      query: data => ({
        url: '/analytic/waste-distribution',
        method: 'GET',
        data: makeApiPayload(data),
      }),
      extraOptions: {
        takeLatest: true,
      },
      transformResponse: transformWeightResponse,
      keepUnusedDataFor: 300,
    }),
    getRegisteredTraysDistribution: builder.query({
      query: data => ({
        url: '/analytic/trays-distribution',
        method: 'GET',
        data: makeApiPayload(data),
      }),
      extraOptions: {
        takeLatest: true,
      },
      transformResponse: transformCountResponse,
      keepUnusedDataFor: 300,
    }),
    getRegisteredTraysCateringDistribution: builder.query({
      query: data => ({
        url: '/analytic/trays-catering-distribution',
        method: 'GET',
        data: makeApiPayload(data),
      }),
      extraOptions: {
        takeLatest: true,
      },
      transformResponse: transformCountResponse,
      keepUnusedDataFor: 300,
    }),
    getRegisteredContainersDistribution: builder.query({
      query: data => ({
        url: '/analytic/containers-distribution',
        method: 'GET',
        data: makeApiPayload(data),
      }),
      extraOptions: {
        takeLatest: true,
      },
      transformResponse: transformCountResponse,
      keepUnusedDataFor: 300,
    }),
    getRegisteredContainersCateringDistribution: builder.query({
      query: data => ({
        url: '/analytic/catering-containers-distribution',
        method: 'GET',
        data: makeApiPayload(data),
      }),
      extraOptions: {
        takeLatest: true,
      },
      transformResponse: transformCountResponse,
      keepUnusedDataFor: 300,
    }),
    getRecommendedTraysDistribution: builder.query({
      query: data => ({
        url: '/analytic/trays-recommended-distribution',
        method: 'GET',
        data: makeApiPayload(data),
      }),
      extraOptions: {
        takeLatest: true,
      },
      transformResponse: transformCountResponse,
      keepUnusedDataFor: 300,
    }),
    getRecommendedTraysCateringDistribution: builder.query({
      query: data => ({
        url: '/analytic/trays-recommended-catering-distribution',
        method: 'GET',
        data: makeApiPayload(data),
      }),
      extraOptions: {
        takeLatest: true,
      },
      transformResponse: transformCountResponse,
      keepUnusedDataFor: 300,
    }),
    getRecommendedContainersDistribution: builder.query({
      query: data => ({
        url: '/analytic/recommended-containers-distribution',
        method: 'GET',
        data: makeApiPayload(data),
      }),
      extraOptions: {
        takeLatest: true,
      },
      transformResponse: transformCountResponse,
      keepUnusedDataFor: 300,
    }),
    getRecommendedContainersCateringDistribution: builder.query({
      query: data => ({
        url: '/analytic/recommended-catering-containers-distribution',
        method: 'GET',
        data: makeApiPayload(data),
      }),
      extraOptions: {
        takeLatest: true,
      },
      transformResponse: transformCountResponse,
      keepUnusedDataFor: 300,
    }),
    getRecommendationsDistribution: builder.query({
      query: data => ({
        url: '/analytic/recommendations-distribution',
        method: 'GET',
        data: makeApiPayload(data),
      }),
      extraOptions: {
        takeLatest: true,
      },
      transformResponse: transformWeightResponse,
      keepUnusedDataFor: 300,
    }),
    getRecommendationsCateringDistribution: builder.query({
      query: data => ({
        url: '/analytic/catering-recommendations-distribution',
        method: 'GET',
        data: makeApiPayload(data),
      }),
      extraOptions: {
        takeLatest: true,
      },
      transformResponse: transformWeightResponse,
      keepUnusedDataFor: 300,
    }),
    getCookedDistribution: builder.query({
      query: data => ({
        url: '/analytic/cooked-distribution',
        method: 'GET',
        data: makeApiPayload(data),
      }),
      extraOptions: {
        takeLatest: true,
      },
      transformResponse: transformWeightResponse,
      keepUnusedDataFor: 300,
    }),
    getCookedCateringDistribution: builder.query({
      query: data => ({
        url: '/analytic/catering-cooked-distribution',
        method: 'GET',
        data: makeApiPayload(data),
      }),
      extraOptions: {
        takeLatest: true,
      },
      transformResponse: transformWeightResponse,
      keepUnusedDataFor: 300,
    }),
    getTaskDelayDistribution: builder.query({
      query: data => ({
        url: '/analytic/combined-task-delay-distribution/total',
        method: 'GET',
        data: makeApiPayload(data),
      }),
      extraOptions: {
        takeLatest: true,
      },
      transformResponse: res => {
        const distributions = res.distributions.map(item => ({
          ...item,
          breadingHighTaskDelay: formatMsToMinutes(item.breadingHighTaskDelay),
          breadingLowTaskDelay: formatMsToMinutes(item.breadingLowTaskDelay),
          breadingAverageTaskDelay: formatMsToMinutes(item.breadingAverageTaskDelay),
          breadingTotalDelay: formatMsToMinutes(item.breadingTotalDelay),
          breadingTotalDelayShare: item.breadingTotalDelay / res.total.breading.totalDelay,
          breadingDelaysNumberShare: item.breadingDelaysNumber / res.total.breading.delaysNumber,
          netHighTaskDelay: formatMsToMinutes(item.netHighTaskDelay),
          netLowTaskDelay: formatMsToMinutes(item.netLowTaskDelay),
          netAverageTaskDelay: formatMsToMinutes(item.netAverageTaskDelay),
          netTotalDelay: formatMsToMinutes(item.netTotalDelay),
          netTotalDelayShare: item.netTotalDelay / res.total.net.totalDelay,
          netDelaysNumberShare: item.netDelaysNumber / res.total.net.delaysNumber,
        }));

        return {
          distributions,
          total: {
            breading: {
              totalDelay: formatMsToMinutes(res.total.breading.totalDelay),
              high: formatMsToMinutes(res.total.breading.high),
              low: formatMsToMinutes(res.total.breading.low),
              average: formatMsToMinutes(res.total.breading.average),
              delaysNumber: res.total.breading.delaysNumber,
            },
            net: {
              totalDelay: formatMsToMinutes(res.total.net.totalDelay),
              high: formatMsToMinutes(res.total.net.high),
              low: formatMsToMinutes(res.total.net.low),
              average: formatMsToMinutes(res.total.net.average),
              delaysNumber: res.total.net.delaysNumber,
            },
          },
        };
      },
      keepUnusedDataFor: 300,
    }),
    getEntreeChutesDistribution: builder.query({
      query: data => ({
        url: '/analytic/entree-chutes-distribution',
        method: 'GET',
        data: makeApiPayload(data),
      }),
      extraOptions: {
        takeLatest: true,
      },
      transformResponse: res => {
        const totalPackages = sumBy(res, 'packagesCount');
        const totalQuantity = sumBy(res, 'quantity');

        const distributions = res.map(item => {
          const { productName, packagesCount, quantity, ...rest } = item;

          return {
            ...rest,
            material: productName,
            packages: packagesCount,
            packagesShare: totalPackages ? packagesCount / totalPackages : 0,
            quantity: quantity,
            quantityShare: totalQuantity ? quantity / totalQuantity : 0,
          };
        });

        return {
          distributions,
          total: {
            packages: formatFloat(totalPackages),
            quantity: formatFloat(totalQuantity),
          },
        };
      },
      keepUnusedDataFor: 300,
    }),
    getUnadjustedHoldTimes: builder.query({
      query: data => ({
        url: '/analytic/timers',
        method: 'GET',
        data: makeApiPayload(data),
      }),
      extraOptions: {
        takeLatest: true,
      },
      keepUnusedDataFor: 300,
    }),
    getAdjustedHoldTimes: builder.query({
      query: data => ({
        url: '/analytic/entree-chutes-holding',
        method: 'GET',
        data: makeApiPayload(data),
      }),
      extraOptions: {
        takeLatest: true,
      },
      keepUnusedDataFor: 300,
    }),
    getCookedIntervals: builder.query({
      query: data => ({
        url: '/analytic/cooked-with-total',
        method: 'GET',
        data: makeApiPayload(data),
      }),
      extraOptions: {
        takeLatest: true,
      },
      transformResponse: transformCombinedIntervalResponse,
      keepUnusedDataFor: 300,
    }),
    getCookedCateringIntervals: builder.query({
      query: data => ({
        url: '/analytic/catering-cooked-with-total',
        method: 'GET',
        data: makeApiPayload(data),
      }),
      extraOptions: {
        takeLatest: true,
      },
      transformResponse: transformCombinedIntervalResponse,
      keepUnusedDataFor: 300,
    }),
    getRecommendationIntervals: builder.query({
      query: data => ({
        url: '/analytic/recommendations-with-total',
        method: 'GET',
        data: makeApiPayload(data),
      }),
      extraOptions: {
        takeLatest: true,
      },
      transformResponse: transformCombinedIntervalResponse,
      keepUnusedDataFor: 300,
    }),
    getRecommendationCateringIntervals: builder.query({
      query: data => ({
        url: '/analytic/catering-recommendations-with-total',
        method: 'GET',
        data: makeApiPayload(data),
      }),
      extraOptions: {
        takeLatest: true,
      },
      transformResponse: transformCombinedIntervalResponse,
      keepUnusedDataFor: 300,
    }),
    getWasteIntervals: builder.query({
      query: data => ({
        url: '/analytic/waste-with-total',
        method: 'GET',
        data: makeApiPayload(data),
      }),
      extraOptions: {
        takeLatest: true,
      },
      transformResponse: transformCombinedIntervalResponse,
      keepUnusedDataFor: 300,
    }),
    getStockOutIntervals: builder.query({
      query: data => ({
        url: '/stockOut/analytic',
        method: 'GET',
        data: makeApiPayload(data),
      }),
      extraOptions: {
        takeLatest: true,
      },
      transformResponse: (...params) => {
        return transformIntervalResponse(...params, value => formatSecondsToMinutes(value));
      },
      keepUnusedDataFor: 300,
    }),
    getNetTaskDelayIntervals: builder.query({
      query: data => ({
        url: '/analytic/recommendations-net-task-delay',
        method: 'GET',
        data: makeApiPayload(data),
      }),
      extraOptions: {
        takeLatest: true,
      },
      transformResponse: (...params) => {
        return transformIntervalResponse(...params, value => formatMsToMinutes(value));
      },
      keepUnusedDataFor: 300,
    }),
    getTotalTaskDelayIntervals: builder.query({
      query: data => ({
        url: '/analytic/recommendations-total-task-delay',
        method: 'GET',
        data: makeApiPayload(data),
      }),
      extraOptions: {
        takeLatest: true,
      },
      transformResponse: (...params) => {
        return transformIntervalResponse(...params, value => formatMsToMinutes(value));
      },
      keepUnusedDataFor: 300,
    }),
    getFryerCycleIntervals: builder.query({
      query: data => ({
        url: '/analytic/fryer-cycles',
        method: 'GET',
        data: makeApiPayload(data),
      }),
      extraOptions: {
        takeLatest: true,
      },
      transformResponse: transformIntervalResponse,
      keepUnusedDataFor: 300,
    }),
    getFryersAvailability: builder.query({
      query: data => ({
        url: '/availability/fryers-with-total',
        method: 'GET',
        data: makeApiPayload(data),
      }),
      extraOptions: {
        takeLatest: true,
      },
      keepUnusedDataFor: 300,
    }),
    getEntreeChutePackageIntervals: builder.query({
      query: data => ({
        url: '/analytic/entree-chutes-packages',
        method: 'GET',
        data: makeApiPayload(data),
      }),
      extraOptions: {
        takeLatest: true,
      },
      transformResponse: transformIntervalResponse,
      keepUnusedDataFor: 300,
    }),
    getEntreeChuteQuantityIntervals: builder.query({
      query: data => ({
        url: '/analytic/entree-chutes-quantity',
        method: 'GET',
        data: makeApiPayload(data),
      }),
      extraOptions: {
        takeLatest: true,
      },
      transformResponse: transformIntervalResponse,
      keepUnusedDataFor: 300,
    }),
    getRegisteredContainersIntervals: builder.query({
      query: data => ({
        url: '/analytic/containers-with-total',
        method: 'GET',
        data: makeApiPayload(data),
      }),
      extraOptions: {
        takeLatest: true,
      },
      transformResponse: transformCombinedIntervalResponse,
      keepUnusedDataFor: 300,
    }),
    getRegisteredContainersCateringIntervals: builder.query({
      query: data => ({
        url: '/analytic/catering-containers-with-total',
        method: 'GET',
        data: makeApiPayload(data),
      }),
      extraOptions: {
        takeLatest: true,
      },
      transformResponse: transformCombinedIntervalResponse,
      keepUnusedDataFor: 300,
    }),
    getRecommendedContainersIntervals: builder.query({
      query: data => ({
        url: '/analytic/recommended-containers-with-total',
        method: 'GET',
        data: makeApiPayload(data),
      }),
      extraOptions: {
        takeLatest: true,
      },
      transformResponse: transformCombinedIntervalResponse,
      keepUnusedDataFor: 300,
    }),
    getRecommendedContainersCateringIntervals: builder.query({
      query: data => ({
        url: '/analytic/recommended-catering-containers-with-total',
        method: 'GET',
        data: makeApiPayload(data),
      }),
      extraOptions: {
        takeLatest: true,
      },
      transformResponse: transformCombinedIntervalResponse,
      keepUnusedDataFor: 300,
    }),
  }),
});

export const {
  useLazyGetInboundConveyorContainersQuery: useLazyGetInboundConveyorContainers,
  useLazyGetOutboundConveyorContainersQuery: useLazyGetOutboundConveyorContainers,
  useGetInboundConveyorDistributionQuery: useGetInboundConveyorDistribution,
  useGetOutboundConveyorDistributionQuery: useGetOutboundConveyorDistribution,
  useGetPerformanceRatioQuery: useGetPerformanceRatio,
  useGetPerformanceTriangleQuery: useGetPerformanceTriangle,
  useGetPerformanceTriangleObjectivesQuery: useGetPerformanceTriangleObjectives,
  usePostPerformanceTriangleObjectivesMutation: usePostPerformanceTriangleObjectives,
  useGetPRSDistributionQuery: useGetPRSDistribution,
  useLazyGetPRSContainersQuery: useLazyGetPRSContainers,
  useGetExcessDistributionQuery: useGetExcessDistribution,
  useGetStockOutDistributionQuery: useGetStockOutDistribution,
  useGetFryerCyclesDistributionQuery: useGetFryerCyclesDistribution,
  useGetWasteDistributionQuery: useGetWasteDistribution,
  useGetRegisteredTraysDistributionQuery: useGetRegisteredTraysDistribution,
  useGetRegisteredTraysCateringDistributionQuery: useGetRegisteredTraysCateringDistribution,
  useGetRegisteredContainersDistributionQuery: useGetRegisteredContainersDistribution,
  useGetRegisteredContainersCateringDistributionQuery: useGetRegisteredContainersCateringDistribution,
  useGetRecommendedTraysDistributionQuery: useGetRecommendedTraysDistribution,
  useGetRecommendedTraysCateringDistributionQuery: useGetRecommendedTraysCateringDistribution,
  useGetRecommendedContainersDistributionQuery: useGetRecommendedContainersDistribution,
  useGetRecommendedContainersCateringDistributionQuery: useGetRecommendedContainersCateringDistribution,
  useGetRecommendationsDistributionQuery: useGetRecommendationsDistribution,
  useGetRecommendationsCateringDistributionQuery: useGetRecommendationsCateringDistribution,
  useGetCookedDistributionQuery: useGetCookedDistribution,
  useGetCookedCateringDistributionQuery: useGetCookedCateringDistribution,
  useGetTaskDelayDistributionQuery: useGetTaskDelayDistribution,
  useGetEntreeChutesDistributionQuery: useGetEntreeChutesDistribution,
  useGetUnadjustedHoldTimesQuery: useGetUnadjustedHoldTimes,
  useGetAdjustedHoldTimesQuery: useGetAdjustedHoldTimes,
  useGetCookedIntervalsQuery: useGetCookedIntervals,
  useGetCookedCateringIntervalsQuery: useGetCookedCateringIntervals,
  useGetRecommendationIntervalsQuery: useGetRecommendationIntervals,
  useGetRecommendationCateringIntervalsQuery: useGetRecommendationCateringIntervals,
  useGetWasteIntervalsQuery: useGetWasteIntervals,
  useGetStockOutIntervalsQuery: useGetStockOutIntervals,
  useGetNetTaskDelayIntervalsQuery: useGetNetTaskDelayIntervals,
  useGetTotalTaskDelayIntervalsQuery: useGetTotalTaskDelayIntervals,
  useGetFryerCycleIntervalsQuery: useGetFryerCycleIntervals,
  useGetFryersAvailabilityQuery: useGetFryersAvailability,
  useGetEntreeChutePackageIntervalsQuery: useGetEntreeChutePackageIntervals,
  useGetEntreeChuteQuantityIntervalsQuery: useGetEntreeChuteQuantityIntervals,
  useGetRegisteredContainersIntervalsQuery: useGetRegisteredContainersIntervals,
  useGetRegisteredContainersCateringIntervalsQuery: useGetRegisteredContainersCateringIntervals,
  useGetRecommendedContainersIntervalsQuery: useGetRecommendedContainersIntervals,
  useGetRecommendedContainersCateringIntervalsQuery: useGetRecommendedContainersCateringIntervals,
} = dashboardAnalyticsApi;

export default dashboardAnalyticsApi;
